<template>
	<div class="d-job-list-custom">
		<ListPageLayout
			v-bind="{
				...$attrs,
				...$props,
			}"
			:search-config="{
				limit: {
					initial: showJobAgent ? 11 : 12,
					value: 12,
				},
				searchResponseTransformerMethod,
			}"
		>
			<!-- The search filters -->
			<template #filters="{ filters, filterValues, updateValue }">
				<div
					v-if="filters && (filters.category || filters.text)"
					class="flex flex-wrap gap-x-layout-gutter gap-y-sm mt-2xl"
				>
					<BaseInputSearch
						v-if="filters.text"
						v-model="textValue"
						class=">=1024:w-6/12col w-full"
						v-bind="filters.text"
						@search="filters.text.value = $event"
					/>
					<BaseInputDropdown
						v-if="filters.category"
						id="dropdown-job"
						class=">=1024:w-6/12col w-full z-10"
						v-bind="filters.category"
						:value="filterValues.category"
						:options="filters.category.value"
						@input="(value) => updateValue('category', value)"
					/>
				</div>
			</template>

			<template #resultsStatus="slotProps">
				Vi har fundet
				{{
					slotProps.pagination && slotProps.pagination.total
						? slotProps.pagination.total
						: 0
				}}
				job{{
					slotProps.pagination && slotProps.pagination.total === 1
						? ''
						: 's'
				}}
			</template>

			<template #results="{ data }">
				<div
					v-if="data && data.length > 0"
					key="content"
					:class="[
						'grid gap-layout-gutter',
						'grid-cols-1 >=768:grid-cols-2 >=1024:grid-cols-3',
					]"
				>
					<template v-for="item in data">
						<!-- Job agent tile -->
						<div
							v-if="item.isJobAgentTile"
							:key="`item-${item.id}`"
							class="
								bg-secondary
								px-xl
								py-lg
								flex flex-col
								items-start
							"
						>
							<h2
								v-if="item.title"
								:class="[
									'font-darker-grotesque font-bold',
									'text-title-card ',
								]"
								v-text="item.title"
							></h2>

							<p
								v-if="item.text"
								class="mt-md text-teaser-sm"
								v-text="item.text"
							></p>

							<NuxtLinkExt
								v-if="item.link"
								:to="item.link.url"
								:target="item.link.target"
								class="mt-auto"
							>
								<BaseButton
									tag="div"
									light
									class="inline-block mt-lg"
									inner-class="bg-transparent"
								>
									{{ item.link.name }}

									<template #icon>
										<SvgIconArrow
											:class="
												isExternal(item.link.url) &&
												'transform -rotate-45'
											"
										/>
									</template>
								</BaseButton>
							</NuxtLinkExt>
						</div>

						<!-- Normal tiles -->
						<ContentCard
							v-else
							:key="`item-${item.id}`"
							v-bind="{
								title: item.title || item.name,
								link: { url: item.url, target: item.target },
							}"
						>
							<template #before>
								<div
									:class="[
										'mb-2xs',
										'font-darker-grotesque font-semibold text-label-card',
									]"
									v-text="item.categories.join(', ')"
								></div>
							</template>

							<template #after>
								<ul
									v-if="formatDate(item.applicationDeadline)"
									class="mt-md"
								>
									<li class="text-teaser-sm">
										<strong>Ansøgningsfrist</strong>
										{{
											formatDate(item.applicationDeadline)
										}}
									</li>
								</ul>
								<div class="flex items-center mt-lg">
									<div
										class="
											w-24
											h-24
											border-black
											bg-black
											text-white
											rounded-full
											flex
											justify-center
											items-center
											mr-12
										"
										style="border-width: 1.5px"
									>
										<SvgIconArrow class="w-12" />
									</div>
									<span
										class="font-darker-grotesque font-bold"
									>
										LÆS MERE
									</span>
								</div>
							</template>
						</ContentCard>
					</template>
				</div>
			</template>
		</ListPageLayout>

		<FeedbackModule
			v-if="!hideFeedback"
			:feedback="feedback"
			class="mt-7xl"
		/>
	</div>
</template>

<script>
import ListPageLayout from '~/components/shared/ListPageLayout';
import ContentCard from '~/components/shared/ContentCard';
import FeedbackModule from '~/components/shared/FeedbackModule';
import SvgIconArrow from '~/assets/svgs/icon-arrow.svg?inline';

export default {
	name: 'MainJobListCustom',

	components: {
		ListPageLayout,
		ContentCard,
		FeedbackModule,
		SvgIconArrow,
	},

	inheritAttrs: false,

	props: {
		searchFilters: Object,
		searchConfig: {
			type: Object,
			default: () => ({}),
		},
		jobAgentTile: {
			type: Object,
			default: null,
		},
		hideFeedback: {
			type: Boolean,
			default: false,
		},
		feedback: Object,
	},
	data() {
		return {
			textValue:
				this.searchFilters?.fields?.find((e) => e.name === 'text')
					.value || '',
		};
	},
	computed: {
		computedFilters() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = element;
			});
			return response;
		},
		showJobAgent() {
			return !!this.jobAgentTile?.showAgent;
		},
	},

	watch: {
		'computedFilters.text.value': {
			immediate: true,
			handler(value) {
				this.textValue = value;
			},
		},
	},
	methods: {
		isExternal(url) {
			if (url) {
				if (
					['http://', 'https://', 'ftp://'].some(
						(str) => url.indexOf(str) === 0
					)
				) {
					return true;
				}

				const splitOut = url.split('/');
				if (splitOut[0].indexOf('.') >= 0) {
					return true;
				}
			}

			return false;
		},
		formatDate(dateString) {
			const date = new Date(dateString);

			const day = `0${date.getDate()}`.slice(-2);
			const month = `0${date.getMonth() + 1}`.slice(-2);
			const year = date.getFullYear();

			if (isNaN(day) || isNaN(month) || isNaN(year)) {
				return dateString;
			}

			return +year <= 1970 ? '' : `${day}.${month}.${year}`;
		},
		searchResponseTransformerMethod(res) {
			// Insert job agent
			if (this.showJobAgent && res?.data?.pagination?.offset === 0) {
				res.data.data = res.data.data || [];
				res.data.data.splice(2, 0, {
					isJobAgentTile: true,
					...this.jobAgentTile,
				});
			}
			return res;
		},
	},
};
</script>
